import React, { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../../supabaseClient';
import {
  consolidateRiskLevel,
  getCompanySize,
  getSHTRequired,
  getSHTLabel,
  getParitarioType,
  getParitarioLabel,
  type RiskLevel,
} from '../../lib/sst-rules';
import { filasDeseadas, guardarActividadesCiiu, mismasActividades, type FilaCiiu } from '../../lib/ciiu-guardar';
import { modalidadPorDefecto } from '../../lib/figuras-sst';
import './onboarding-pasos.css';

interface CiiuActivity {
  code: string;
  description: string;
  section: string;
  section_name: string;
  risk_level: RiskLevel;
  am196_ref: string | null;
}

interface Props {
  companyId: string;
  onNext: () => void;
  onBack: () => void;
  // PE-A4d: 'perfil' = abierto desde el menú (Perfil SSO): botones "Cancelar" (vuelve sin guardar) y "Guardar", sin
  // "Omitir por ahora". 'onboarding' (por defecto) = paso del asistente de bienvenida.
  modo?: 'onboarding' | 'perfil';
}

// Fila guardada en company_ciiu_activities con su actividad del catálogo ciiu_activities.
interface CiiuGuardada { ciiu_code: string; is_primary: boolean; ciiu_activities: CiiuActivity | CiiuActivity[] | null }

const RISK_COLORS: Record<RiskLevel, { bg: string; color: string; border: string }> = {
  bajo:  { bg: 'rgba(34,197,94,0.1)',  color: '#22c55e', border: 'rgba(34,197,94,0.3)'  },
  medio: { bg: 'rgba(245,158,11,0.1)', color: '#f59e0b', border: 'rgba(245,158,11,0.3)' },
  alto:  { bg: 'rgba(239,68,68,0.1)',  color: '#ef4444', border: 'rgba(239,68,68,0.3)'  },
};

const RISK_LABELS: Record<RiskLevel, string> = {
  bajo: 'Bajo', medio: 'Medio', alto: 'Alto',
};

export default function StepCIIU({ companyId, onNext, onBack, modo = 'onboarding' }: Props) {
  const enPerfil = modo === 'perfil';
  const [query, setQuery]                   = useState('');
  const [results, setResults]               = useState<CiiuActivity[]>([]);
  const [showDropdown, setShowDropdown]     = useState(false);
  const [primary, setPrimary]               = useState<CiiuActivity | null>(null);
  const [secondaries, setSecondaries]       = useState<CiiuActivity[]>([]);
  const [workers, setWorkers]               = useState<number>(1);
  const [hasRemote, setHasRemote]           = useState(false);
  const [hasPriority, setHasPriority]       = useState(false);
  const [companyType, setCompanyType]                         = useState<'publica' | 'privada'>('privada');
  const [hasHighRiskConstruction, setHasHighRiskConstruction] = useState(false);
  const [hasHighRiskElectrical, setHasHighRiskElectrical]     = useState(false);
  const [hasVehicles, setHasVehicles]                         = useState(false);
  const [hasContractors, setHasContractors]                   = useState(false);
  const [workCentersCount, setWorkCentersCount]               = useState<number>(1);
  const [priorityGroups, setPriorityGroups]                   = useState({
    adultos_mayores: false,
    lactancia: false,
    embarazadas: false,
    discapacidad: false,
    enfermedades_catastroficas: false,
  });
  const [showTooltip, setShowTooltip]       = useState(false);
  const [saving, setSaving]                 = useState(false);
  const [error, setError]                   = useState('');
  // PE-A4d: lo guardado (CIIU y perfil) se precarga; mientras carga no se puede guardar, para no pisar el perfil con los
  // valores por defecto (1 trabajador, todo en "no").
  const [cargando, setCargando]             = useState(true);
  const [sinCiiuGuardado, setSinCiiuGuardado] = useState(false);
  // F1a2: lo que mostró la precarga; si al guardar la selección es la misma, company_ciiu_activities no se toca.
  const ciiuInicial                         = useRef<FilaCiiu[] | null>(null);
  const searchRef                          = useRef<HTMLDivElement>(null);
  const debounceRef                         = useRef<ReturnType<typeof setTimeout> | null>(null);

  const allSelected = primary ? [primary, ...secondaries] : [];
  const allRisks = allSelected.map((a) => a.risk_level);
  const consolidatedRisk: RiskLevel = allRisks.length > 0 ? consolidateRiskLevel(allRisks) : 'bajo';
  const hasMultipleRiskLevels = new Set(allRisks).size > 1;

  const companySize = getCompanySize(workers);
  const shtRequired = getSHTRequired(companySize, consolidatedRisk);
  const paritarioType = getParitarioType(workers);

  const companySizeLabels: Record<string, string> = {
    micro: 'Microempresa (1–9 trabajadores)',
    pequeña: 'Pequeña empresa (10–49 trabajadores)',
    mediana_a: 'Mediana empresa A (50–99 trabajadores)',
    mediana_b: 'Mediana empresa B (100–199 trabajadores)',
    grande: 'Gran empresa (200+ trabajadores)',
  };

  const searchCiiu = useCallback(async (q: string) => {
    if (q.length < 2) { setResults([]); setShowDropdown(false); return; }
    const { data } = await supabase
      .from('ciiu_activities')
      .select('code, description, section, section_name, risk_level, am196_ref')
      .or(`code.ilike.%${q}%,description.ilike.%${q}%`)
      .limit(8);
    setResults(data ?? []);
    setShowDropdown(true);
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchCiiu(query), 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, searchCiiu]);

  // Precarga: actividades CIIU (company_ciiu_activities + ciiu_activities) y el perfil SSO de la empresa.
  useEffect(() => {
    if (!companyId) { setCargando(false); return; }
    let vivo = true;
    (async () => {
      const [{ data: acts, error: e1 }, { data: perfil, error: e2 }] = await Promise.all([
        supabase.from('company_ciiu_activities')
          .select('ciiu_code, is_primary, ciiu_activities(code, description, section, section_name, risk_level, am196_ref)')
          .eq('company_id', companyId)
          .order('is_primary', { ascending: false }),
        supabase.from('company_sso_profile')
          .select('workers_count, has_remote_workers, has_priority_groups, company_type, has_high_risk_construction, has_high_risk_electrical, has_vehicles, has_contractors, work_centers_count, priority_groups')
          .eq('company_id', companyId)
          .maybeSingle(),
      ]);
      if (!vivo) return;
      if (e1 || e2) console.error('StepCIIU: no se pudo leer el perfil guardado', e1 ?? e2);
      const guardadas = ((acts ?? []) as unknown as CiiuGuardada[])
        .map(f => ({ principal: f.is_primary, act: Array.isArray(f.ciiu_activities) ? f.ciiu_activities[0] : f.ciiu_activities }))
        .filter((f): f is { principal: boolean; act: CiiuActivity } => !!f.act);
      const principal = guardadas.find(f => f.principal)?.act ?? guardadas[0]?.act ?? null;
      const secundarias = guardadas.map(f => f.act).filter(a => a.code !== principal?.code).slice(0, 3);
      setPrimary(principal);
      setSecondaries(secundarias);
      if (!e1 && principal) ciiuInicial.current = filasDeseadas(principal.code, secundarias.map(a => a.code));
      setSinCiiuGuardado(!e1 && guardadas.length === 0);
      const p = perfil as Record<string, any> | null;
      if (p) {
        if (typeof p.workers_count === 'number' && p.workers_count > 0) setWorkers(p.workers_count);
        setHasRemote(!!p.has_remote_workers);
        setHasPriority(!!p.has_priority_groups);
        if (p.company_type === 'publica' || p.company_type === 'privada') setCompanyType(p.company_type);
        setHasHighRiskConstruction(!!p.has_high_risk_construction);
        setHasHighRiskElectrical(!!p.has_high_risk_electrical);
        setHasVehicles(!!p.has_vehicles);
        setHasContractors(!!p.has_contractors);
        if (typeof p.work_centers_count === 'number' && p.work_centers_count > 0) setWorkCentersCount(p.work_centers_count);
        if (p.priority_groups && typeof p.priority_groups === 'object') setPriorityGroups(g => ({ ...g, ...p.priority_groups }));
      }
      setCargando(false);
    })();
    return () => { vivo = false; };
  }, [companyId]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  function selectActivity(activity: CiiuActivity) {
    setQuery('');
    setShowDropdown(false);
    if (!primary) { setPrimary(activity); return; }
    if (primary.code === activity.code) return;
    if (secondaries.some((s) => s.code === activity.code)) return;
    if (secondaries.length >= 3) return;
    setSecondaries((prev) => [...prev, activity]);
  }

  function removePrimary() {
    if (secondaries.length > 0) {
      setPrimary(secondaries[0]);
      setSecondaries((prev) => prev.slice(1));
    } else {
      setPrimary(null);
    }
  }

  function removeSecondary(code: string) {
    setSecondaries((prev) => prev.filter((s) => s.code !== code));
  }

  async function handleSave() {
    if (!primary) { setError('Selecciona al menos una actividad principal.'); return; }
    if (workers < 1) { setError('El número de trabajadores debe ser al menos 1.'); return; }
    if (!companyId) { setError('Error: empresa no identificada.'); return; }
    setError('');
    setSaving(true);

    // F1a2: sin borrar y volver a insertar todo. Si la selección es la precargada, no se escribe nada en la tabla;
    // si cambió, se escribe solo la diferencia, sin dejar nunca la empresa sin actividad principal.
    const deseadas = filasDeseadas(primary.code, secondaries.map(s => s.code));
    if (!ciiuInicial.current || !mismasActividades(ciiuInicial.current, deseadas)) {
      const errorCiiu = await guardarActividadesCiiu(companyId, primary.code, secondaries.map(s => s.code));
      if (errorCiiu) {
        setError(errorCiiu);
        setSaving(false);
        return;
      }
      ciiuInicial.current = deseadas;
    }

    // Bloque 6, B: la modalidad SST que eligió la empresa se conserva al editar;
    // si todavía no tiene, se pone la que exige el DE 255 Art. 18.
    const { data: actual } = await supabase
      .from('company_sso_profile')
      .select('modalidad_sst')
      .eq('company_id', companyId)
      .maybeSingle();
    const modalidad = (actual as { modalidad_sst?: string | null } | null)?.modalidad_sst
      ?? (workers > 0 ? modalidadPorDefecto(workers, consolidatedRisk) : null);

    const { error: perfilError } = await supabase.from('company_sso_profile').upsert(
      {
        company_id: companyId,
        modalidad_sst: modalidad,
        workers_count: workers,
        has_remote_workers: hasRemote,
        has_priority_groups: hasPriority,
        risk_level: consolidatedRisk,
        company_size: companySize,
        sht_required: shtRequired,
        paritario_type: paritarioType,
        status: 'pendiente',
        updated_at: new Date().toISOString(),
        company_type:               companyType,
        has_high_risk_construction: hasHighRiskConstruction,
        has_high_risk_electrical:   hasHighRiskElectrical,
        has_vehicles:               hasVehicles,
        has_contractors:            hasContractors,
        work_centers_count:         workCentersCount,
        priority_groups:            priorityGroups,
      },
      { onConflict: 'company_id' }
    );
    if (perfilError) {
      console.error('StepCIIU: no se pudo guardar el perfil SSO', perfilError);
      setError('No se pudo guardar el perfil SSO. Intenta de nuevo (solo el administrador o el gerente pueden cambiarlo).');
      setSaving(false);
      return;
    }

    setSaving(false);
    onNext();
  }

  function RiskBadge({ level }: { level: RiskLevel }) {
    const c = RISK_COLORS[level];
    return (
      <span style={{
        background: c.bg, color: c.color, border: `1px solid ${c.border}`,
        borderRadius: 6, padding: '2px 8px', fontSize: '0.72rem', fontWeight: 700,
        letterSpacing: '0.03em', flexShrink: 0,
      }}>
        {RISK_LABELS[level]}
      </span>
    );
  }

  return (
    <div className="onb-step" style={{ alignItems: 'stretch', textAlign: 'left' }}>
      <div style={{ textAlign: 'center', marginBottom: 20 }}>
        <div className="onb-step-icon" style={{ background: 'rgba(99,102,241,0.12)', color: '#818cf8', margin: '0 auto 12px' }}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          </svg>
        </div>
        <h2 className="onb-title" style={{ textAlign: 'center' }}>Actividad económica y perfil SST</h2>
        <p className="onb-desc" style={{ textAlign: 'center' }}>
          Según el Decreto Ejecutivo 255 y el AM 196, la actividad CIIU determina tu perfil
          de Seguridad y Salud en el Trabajo.
        </p>
      </div>

      {cargando && (
        <p role="status" style={{ color: '#94a3b8', fontSize: '0.84rem', margin: '0 0 12px', textAlign: 'center' }}>Cargando el perfil guardado…</p>
      )}
      {!cargando && sinCiiuGuardado && (
        <div role="status" style={alertStyle}>
          <span style={{ fontSize: '1rem', marginRight: 8 }} aria-hidden>⚠️</span>
          <span>
            Tu empresa no tiene una actividad CIIU guardada. Elige la actividad principal para poder
            {enPerfil ? ' guardar el perfil' : ' continuar'}: de ella sale el nivel de riesgo (DE 255 Art. 49).
          </span>
        </div>
      )}

      {/* CIIU Search */}
      <label style={labelStyle}>Actividad económica CIIU</label>
      <div ref={searchRef} style={{ position: 'relative', marginBottom: 8 }}>
        <input
          type="text"
          placeholder="Buscar por código o descripción (mín. 2 caracteres)…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={inputStyle}
        />
        {showDropdown && results.length > 0 && (
          <div style={dropdownStyle}>
            {results.map((act) => (
              <button
                key={act.code}
                onClick={() => selectActivity(act)}
                style={dropdownItemStyle}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.06)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                <span style={{ fontWeight: 700, color: '#e2e8f0', marginRight: 8, flexShrink: 0 }}>
                  {act.code}
                </span>
                <span style={{ color: '#94a3b8', fontSize: '0.82rem', flex: 1, textAlign: 'left' }}>
                  {act.description}
                </span>
                <RiskBadge level={act.risk_level} />
              </button>
            ))}
          </div>
        )}
        {showDropdown && query.length >= 2 && results.length === 0 && (
          <div style={{ ...dropdownStyle, padding: '12px 16px', color: '#64748b', fontSize: '0.84rem' }}>
            Sin resultados para "{query}"
          </div>
        )}
      </div>

      {/* Selected activities */}
      {allSelected.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          {primary && (
            <div style={activityRowStyle}>
              <span style={{ fontSize: '0.7rem', color: '#2FBF71', fontWeight: 700, minWidth: 64 }}>PRINCIPAL</span>
              <span style={{ fontWeight: 700, color: '#e2e8f0', marginRight: 6 }}>{primary.code}</span>
              <span style={{ color: '#94a3b8', fontSize: '0.82rem', flex: 1 }}>{primary.description}</span>
              <RiskBadge level={primary.risk_level} />
              <button onClick={removePrimary} style={removeBtn} title="Quitar">✕</button>
            </div>
          )}
          {secondaries.map((s) => (
            <div key={s.code} style={activityRowStyle}>
              <span style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 700, minWidth: 64 }}>SECUNDARIA</span>
              <span style={{ fontWeight: 700, color: '#e2e8f0', marginRight: 6 }}>{s.code}</span>
              <span style={{ color: '#94a3b8', fontSize: '0.82rem', flex: 1 }}>{s.description}</span>
              <RiskBadge level={s.risk_level} />
              <button onClick={() => removeSecondary(s.code)} style={removeBtn} title="Quitar">✕</button>
            </div>
          ))}
          {secondaries.length < 3 && primary && (
            <p style={{ fontSize: '0.75rem', color: '#475569', marginTop: 4 }}>
              Puedes agregar hasta {3 - secondaries.length} actividad{3 - secondaries.length !== 1 ? 'es' : ''} secundaria{3 - secondaries.length !== 1 ? 's' : ''} más.
            </p>
          )}
        </div>
      )}

      {/* Multi-risk alert */}
      {hasMultipleRiskLevels && (
        <div style={alertStyle}>
          <span style={{ fontSize: '1rem', marginRight: 8 }}>⚠️</span>
          <span>
            Tus actividades tienen distintos niveles de riesgo. Se aplica el nivel más alto
            (<strong style={{ color: RISK_COLORS[consolidatedRisk].color }}>{RISK_LABELS[consolidatedRisk]}</strong>)
            por criterio preventivo — DE 255 Art. 49.
          </span>
        </div>
      )}

      {/* Workers count */}
      <label style={labelStyle}>Número total de trabajadores</label>
      <input
        type="number"
        min={1}
        value={workers}
        onChange={(e) => setWorkers(Math.max(1, parseInt(e.target.value) || 1))}
        style={{ ...inputStyle, marginBottom: 12 }}
      />

      {/* Toggles */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
        <label style={toggleRowStyle}>
          <div style={toggleTrack(hasRemote)} onClick={() => setHasRemote((v) => !v)}>
            <div style={toggleThumb(hasRemote)} />
          </div>
          <span style={{ color: '#cbd5e1', fontSize: '0.86rem' }}>Incluye teletrabajadores</span>
        </label>

        <label style={toggleRowStyle}>
          <div style={toggleTrack(hasPriority)} onClick={() => setHasPriority((v) => !v)}>
            <div style={toggleThumb(hasPriority)} />
          </div>
          <span style={{ color: '#cbd5e1', fontSize: '0.86rem' }}>
            Incluye grupos de atención prioritaria
          </span>
          <div style={{ position: 'relative', display: 'inline-block', marginLeft: 6 }}>
            <button
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
              style={{ background: 'none', border: 'none', cursor: 'help', color: '#64748b', padding: 0, lineHeight: 1 }}
            >
              ⓘ
            </button>
            {showTooltip && (
              <div style={{
                position: 'absolute', bottom: 'calc(100% + 6px)', left: '50%',
                transform: 'translateX(-50%)', background: '#0f172a', border: '1px solid #334155',
                borderRadius: 8, padding: '10px 14px', zIndex: 100,
                boxShadow: '0 4px 16px rgba(0,0,0,0.5)', fontSize: '0.78rem', color: '#94a3b8',
                maxWidth: 260, whiteSpace: 'normal',
              }}>
                Adultos mayores, personas con discapacidad, mujeres embarazadas u otras
                condiciones de vulnerabilidad. Requiere medidas adicionales según DE 255 Art. 15.
              </div>
            )}
          </div>
        </label>
      </div>

      {/* Company type */}
      <label style={labelStyle}>Tipo de empresa</label>
      <select
        value={companyType}
        onChange={(e) => setCompanyType(e.target.value as 'publica' | 'privada')}
        style={{ ...inputStyle, marginBottom: 16 }}
      >
        <option value="privada">Empresa privada</option>
        <option value="publica">Empresa pública</option>
      </select>

      {/* Work centers count */}
      <label style={labelStyle}>Número de centros de trabajo</label>
      <input
        type="number"
        min={1}
        value={workCentersCount}
        onChange={(e) => setWorkCentersCount(Math.max(1, parseInt(e.target.value) || 1))}
        style={{ ...inputStyle, marginBottom: 16 }}
      />

      {/* Additional toggles */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
        <label style={toggleRowStyle}>
          <div style={toggleTrack(hasVehicles)} onClick={() => setHasVehicles((v) => !v)}>
            <div style={toggleThumb(hasVehicles)} />
          </div>
          <span style={{ color: '#cbd5e1', fontSize: '0.86rem' }}>Opera vehículos a motor o maquinaria</span>
        </label>
        <label style={toggleRowStyle}>
          <div style={toggleTrack(hasContractors)} onClick={() => setHasContractors((v) => !v)}>
            <div style={toggleThumb(hasContractors)} />
          </div>
          <span style={{ color: '#cbd5e1', fontSize: '0.86rem' }}>Tiene contratistas o subcontratistas</span>
        </label>
        <label style={toggleRowStyle}>
          <div style={toggleTrack(hasHighRiskConstruction)} onClick={() => setHasHighRiskConstruction((v) => !v)}>
            <div style={toggleThumb(hasHighRiskConstruction)} />
          </div>
          <span style={{ color: '#cbd5e1', fontSize: '0.86rem' }}>Realiza actividades de construcción (alto riesgo)</span>
        </label>
        <label style={toggleRowStyle}>
          <div style={toggleTrack(hasHighRiskElectrical)} onClick={() => setHasHighRiskElectrical((v) => !v)}>
            <div style={toggleThumb(hasHighRiskElectrical)} />
          </div>
          <span style={{ color: '#cbd5e1', fontSize: '0.86rem' }}>Realiza actividades eléctricas de alto riesgo</span>
        </label>
      </div>

      {/* Priority groups breakdown */}
      {hasPriority && (
        <div style={{ marginTop: 8, marginBottom: 16, paddingLeft: 16, borderLeft: '2px solid #334155' }}>
          <p style={{ color: '#94a3b8', fontSize: '0.8rem', marginBottom: 8 }}>Selecciona los grupos presentes:</p>
          {[
            { key: 'adultos_mayores',            label: 'Adultos mayores' },
            { key: 'lactancia',                  label: 'Mujeres en período de lactancia' },
            { key: 'embarazadas',                label: 'Mujeres embarazadas' },
            { key: 'discapacidad',               label: 'Trabajadores con discapacidad' },
            { key: 'enfermedades_catastroficas',  label: 'Enfermedades catastróficas o alta complejidad' },
          ].map(({ key, label }) => (
            <label key={key} style={toggleRowStyle}>
              <div
                style={toggleTrack(priorityGroups[key as keyof typeof priorityGroups])}
                onClick={() => setPriorityGroups(prev => ({ ...prev, [key]: !prev[key as keyof typeof priorityGroups] }))}
              >
                <div style={toggleThumb(priorityGroups[key as keyof typeof priorityGroups])} />
              </div>
              <span style={{ color: '#cbd5e1', fontSize: '0.86rem' }}>{label}</span>
            </label>
          ))}
        </div>
      )}

      {/* Result card */}
      {primary && (
        <div style={resultCardStyle}>
          <p style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 700, marginBottom: 12, letterSpacing: '0.05em' }}>
            PERFIL SST AUTOMÁTICO
          </p>
          <div style={resultRowStyle}>
            <span style={{ color: '#94a3b8', fontSize: '0.82rem' }}>Tamaño empresa</span>
            <span style={{ color: '#e2e8f0', fontWeight: 600, fontSize: '0.84rem' }}>{companySizeLabels[companySize]}</span>
          </div>
          <div style={resultRowStyle}>
            <span style={{ color: '#94a3b8', fontSize: '0.82rem' }}>Nivel de riesgo</span>
            <RiskBadge level={consolidatedRisk} />
          </div>
          <div style={{ ...resultRowStyle, alignItems: 'flex-start' }}>
            <span style={{ color: '#94a3b8', fontSize: '0.82rem', flexShrink: 0 }}>Responsable SST</span>
            <div style={{ textAlign: 'right' }}>
              <span style={{ color: '#e2e8f0', fontWeight: 600, fontSize: '0.84rem', display: 'block' }}>
                {getSHTLabel(shtRequired)}
              </span>
              <span style={{ color: '#475569', fontSize: '0.72rem' }}>Art. 18 DE 255</span>
            </div>
          </div>
          <div style={{ ...resultRowStyle, alignItems: 'flex-start' }}>
            <span style={{ color: '#94a3b8', fontSize: '0.82rem', flexShrink: 0 }}>Organismo paritario</span>
            <div style={{ textAlign: 'right' }}>
              <span style={{ color: '#e2e8f0', fontWeight: 600, fontSize: '0.84rem', display: 'block' }}>
                {getParitarioLabel(paritarioType).split(' (')[0]}
              </span>
              <span style={{ color: '#475569', fontSize: '0.72rem' }}>
                {getParitarioLabel(paritarioType).match(/\(([^)]+)\)/)?.[1] ?? ''}
              </span>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div style={{ color: '#ef4444', fontSize: '0.82rem', marginBottom: 8, padding: '8px 12px', background: 'rgba(239,68,68,0.08)', borderRadius: 8, border: '1px solid rgba(239,68,68,0.2)' }}>
          {error}
        </div>
      )}

      <div className="onb-btn-row">
        <button type="button" className="onb-btn-secondary" onClick={onBack} disabled={saving}>
          {enPerfil ? 'Cancelar' : '← Atrás'}
        </button>
        <button type="button" className="onb-btn-primary" onClick={handleSave} disabled={saving || cargando || !primary}>
          {saving ? 'Guardando…' : enPerfil ? 'Guardar' : 'Continuar →'}
        </button>
      </div>
      {!enPerfil && <button type="button" className="onb-btn-skip" onClick={onNext}>Omitir por ahora</button>}
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const labelStyle: React.CSSProperties = {
  display: 'block', color: '#94a3b8', fontSize: '0.8rem', fontWeight: 600,
  marginBottom: 6, letterSpacing: '0.03em',
};

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '10px 12px', background: 'rgba(255,255,255,0.04)',
  border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0', fontSize: '0.86rem',
  outline: 'none', boxSizing: 'border-box',
};

const dropdownStyle: React.CSSProperties = {
  position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
  background: '#1e293b', border: '1px solid #334155', borderRadius: 10,
  boxShadow: '0 8px 24px rgba(0,0,0,0.5)', zIndex: 50, overflow: 'hidden',
};

const dropdownItemStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8, width: '100%',
  padding: '10px 14px', background: 'transparent', border: 'none',
  cursor: 'pointer', transition: 'background 0.15s',
};

const activityRowStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
  background: 'rgba(255,255,255,0.03)', border: '1px solid #334155',
  borderRadius: 8, marginBottom: 6,
};

const removeBtn: React.CSSProperties = {
  background: 'none', border: 'none', color: '#475569', cursor: 'pointer',
  fontSize: '0.8rem', padding: '0 4px', flexShrink: 0, lineHeight: 1,
};

const alertStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'flex-start', gap: 8, padding: '10px 14px',
  background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)',
  borderRadius: 8, marginBottom: 16, fontSize: '0.82rem', color: '#fbbf24',
};

const resultCardStyle: React.CSSProperties = {
  background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.2)',
  borderRadius: 12, padding: '16px', marginBottom: 20,
};

const resultRowStyle: React.CSSProperties = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  gap: 8, paddingBottom: 10, marginBottom: 10,
  borderBottom: '1px solid rgba(255,255,255,0.05)',
};

function toggleTrack(active: boolean): React.CSSProperties {
  return {
    width: 40, height: 22, borderRadius: 11, cursor: 'pointer', flexShrink: 0,
    background: active ? 'rgba(47,191,113,0.3)' : '#334155',
    border: `2px solid ${active ? '#2FBF71' : '#475569'}`,
    position: 'relative', transition: 'all 0.2s',
  };
}

function toggleThumb(active: boolean): React.CSSProperties {
  return {
    width: 14, height: 14, borderRadius: '50%', position: 'absolute',
    top: 2, left: active ? 20 : 2, transition: 'left 0.2s',
    background: active ? '#2FBF71' : '#64748b',
  };
}

const toggleRowStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
};
